import pick from 'lodash.pick';
import Sentry from '@saucelabs/sl-util-sentry';

import * as constants from './constants';

export const getLiveTestingConfig = (formData) => {
  const { deviceType, testType } = formData;
  const {
    CONFIGURATION_TEST_TYPES: { WEB, APP },
    CONFIGURATION_DEVICE_TYPES: { DESKTOP_BROWSER, REAL_DEVICE, VIRTUAL_DEVICE },
  } = constants;
  const commonValues = pick(formData, ['url', 'deviceType', 'testType', 'tunnel']);

  if (testType === WEB) {
    if (deviceType === DESKTOP_BROWSER) {
      return {
        ...commonValues,
        browserConfiguration: formData.browserConfiguration,
        realDeviceConfiguration: formData.realDeviceConfiguration,
        virtualDeviceConfiguration: formData.virtualDeviceConfiguration,
      };
    }
    if (deviceType === VIRTUAL_DEVICE) {
      return {
        ...commonValues,
        browserConfiguration: formData.browserConfiguration,
        realDeviceConfiguration: formData.realDeviceConfiguration,
        virtualDeviceConfiguration: formData.virtualDeviceConfiguration,
      };
    }
    if (deviceType === REAL_DEVICE) {
      return {
        ...commonValues,
        browserConfiguration: formData.browserConfiguration,
        realDeviceConfiguration: formData.realDeviceConfiguration,
        virtualDeviceConfiguration: formData.virtualDeviceConfiguration,
      };
    }
  }

  if (testType === APP) {
    if (deviceType === VIRTUAL_DEVICE) {
      return {
        ...commonValues,
        virtualDeviceConfiguration: formData.virtualDeviceConfiguration,
      };
    }

    if (deviceType === REAL_DEVICE) {
      return {
        ...commonValues,
        realDeviceConfiguration: formData.realDeviceConfiguration,
      };
    }
  }

  return null;
};

export const getTestingConfigs = (
  configs,
  testType,
  deviceTypes = [ // the default is all available types
    constants.CONFIGURATION_DEVICE_TYPES.DESKTOP_BROWSER,
    constants.CONFIGURATION_DEVICE_TYPES.REAL_DEVICE,
    constants.CONFIGURATION_DEVICE_TYPES.VIRTUAL_DEVICE,
  ],
) => (
  configs
    .filter((value) => value.testType === testType)
    .filter((value) => deviceTypes.includes(value.deviceType))
);

/**
 * Returns whether a tunnel is still running.
 * @param {string} tunnelUnderTest - name of the selected tunnel from the dropdown
 * @param {ImmutableMap<string, ImmutableList<TunnelRecord>>} tunnels
 * @returns {boolean} true if tunnel tunnelUnderTest still running, false otherwise
 */
export const isTunnelStillRunning = (tunnelUnderTest, tunnels) => {
  // May 7th, 2020: temporary try/catch to solve issue in #livetesting-bots
  try {
    return tunnels.valueSeq().some((tunnelArray) => (
      tunnelArray.some((tunnel) => (
        tunnel.getIdentifier() === tunnelUnderTest && tunnel.isRunning()),
      )
    ));
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setExtra('tunnels', JSON.stringify(tunnels));
      Sentry.captureException(error);
    });

    return false;
  }
};

export const getTeamIds = (teams) => (
  teams.map((team) => team?.id)
);

/**
 * Gets the extension of a filename. Example: 'CoolApp.apk' -> '.apk'
 * @param {string} fileName
 * @returns {string|undefined} undefined when the string lacks a file extension
 */
export const getExtensionFromFileName = (fileName) => (
  fileName?.match(/\.[a-z0-9]+$/i)?.[0]
);

/**
 * Gets the rounded file size in MB
 * @param {number} fileSize in bytes
 * @returns {number|undefined} undefined when fileSize is not a number
 */
export const getRoundedFileSizeInMB = (sizeInBytes) => {
  if (Number.isFinite(sizeInBytes)) {
    return Math.round(Math.abs(sizeInBytes) / 1000 ** 2);
  }
  return undefined;
};

/**
 * Returns a displayable string that represents the browser based on a VDC test's metadata
 * @param {ImmutableMap} metadata - the metadata of a VDC live test
 * @return {string} - a string representing the browser for the given test's metadata
 */
export const getDisplayableBrowserDescription = (metadata) => {
  if (!metadata?.get('browser')) {
    return ''; // Return an empty string when no browser information is available
  }

  const osVersionDisplay = metadata?.get('osVersionDisplay');
  const browserDisplay = metadata?.get('browserDisplay');
  const browserVersionDisplay = metadata?.get('browserVersionDisplay');

  if (metadata?.get('isMobileVirtual')) {
    return osVersionDisplay ?? '';
  }

  // For desktop browser tests show the browser name and browser version
  if (browserDisplay && browserVersionDisplay) {
    return `${browserDisplay} ${browserVersionDisplay}`;
  }

  return metadata?.get('browser');
};

/**
 * returns the size of the file with nearest possible format
 * @param {number} bytes
 * @param {number} decimals
 */
export const formatBytes = (bytes, decimals = 1) => {
  if (!bytes) return '0 Bytes';

  const unit = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const size = Math.floor(Math.log(bytes) / Math.log(unit));

  // eslint-disable-next-line no-restricted-properties
  return `${parseFloat((bytes / Math.pow(unit, size)).toFixed(dm))} ${sizes[size]}`;
};

/**
 * Returns the number of days until the user's credit refreshes
 * @param {string} creditRefreshDate - date string in ISO format
 * @returns {number}
 */
export const getDaysUntilRefresh = (creditRefreshDate) => {
  const millisUntilRefresh = new Date(creditRefreshDate).getTime() - new Date().getTime();
  const daysUntilRefresh = Math.ceil(millisUntilRefresh / (1000 * 60 * 60 * 24));
  return daysUntilRefresh;
};
